// frontend/src/store/usePriceStore.ts

/**
 * Price Store (Zustand)
 * 
 * Manages real-time price data
 */

import { create } from 'zustand';
import api from '@/lib/api';
import type { PriceData } from '@/types';

interface PriceState {
  prices: Map<string, PriceData>;
  isLoading: boolean;
  lastUpdate: Date | null;
  
  // Actions
  fetchPrices: () => Promise<void>;
  getPrice: (assetId: string) => PriceData | undefined;
  updatePrice: (price: PriceData) => void;
  updatePrices: (prices: PriceData[]) => void;
}

export const usePriceStore = create<PriceState>((set, get) => ({
  prices: new Map(),
  isLoading: false,
  lastUpdate: null,

  fetchPrices: async () => {
    try {
      set({ isLoading: true });
      
      const response = await api.get<{ prices: PriceData[] }>('/prices');
      const pricesMap = new Map<string, PriceData>();
      
      response.data.prices.forEach((price) => {
        pricesMap.set(price.asset_id, price);
      });

      set({
        prices: pricesMap,
        lastUpdate: new Date(),
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      console.error('Failed to fetch prices:', error);
    }
  },

  getPrice: (assetId: string) => {
    return get().prices.get(assetId);
  },

  updatePrice: (price: PriceData) => {
    set((state) => {
      const newPrices = new Map(state.prices);
      newPrices.set(price.asset_id, price);
      return { prices: newPrices, lastUpdate: new Date() };
    });
  },

  updatePrices: (prices: PriceData[]) => {
    set((state) => {
      const newPrices = new Map(state.prices);
      prices.forEach((price) => {
        newPrices.set(price.asset_id, price);
      });
      return { prices: newPrices, lastUpdate: new Date() };
    });
  },
}));
